/**
 * DepositCard — форма пополнения в личном кабинете юзера.
 *
 * Работает в двух режимах платёжки (определяется backend'ом):
 *   - test: одна большая кнопка "Получить 1000 USDT", мгновенный кредит
 *   - production: выбор сети BSC/TON → получаем уникальный депозитный
 *     адрес, показываем QR и инструкции + кнопка "Я оплатил" для
 *     форс-рескана + история пополнений
 *
 * Все тексты на "Вы" по правилам Luna.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation, Trans } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  Copy,
  RefreshCw,
  CheckCircle2,
  Loader2,
  AlertCircle,
  Gift,
  Sparkles,
  X as XIcon,
} from 'lucide-react';
import { GlassSurface, GlassButton } from '@/components/glass';
import { lunaApi } from '@/services/api';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';
import { useUISounds } from '@/hooks/useUISounds';
import type { AxiosResponse } from 'axios';

type Network = 'BSC' | 'TON';

interface DepositHistoryItem {
  id: number;
  amount: string;
  network: 'BSC' | 'TON';
  status: 'detected' | 'confirming' | 'credited' | 'failed';
  confirmations: number;
  tx_hash: string;
  credited_at: string | null;
  created_at: string;
}

interface ProductionWallet {
  id: number;
  address: string;
  network: Network;
}

type WalletResp =
  | { success: true; mode: 'test'; credited: string; newBalance: string; message: string }
  | {
      success: true;
      mode: 'production';
      wallet: { id: number; network: Network; address: string; createdAt: string };
    };

interface Props {
  /** Колбэк после успешного test-deposit / после detected production */
  onBalanceChange?: () => void;
}

export function DepositCard({ onBalanceChange }: Props) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';
  const { haptic } = useTelegramWebApp();
  const uiSounds = useUISounds();
  const [network, setNetwork] = useState<Network>('TON');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'test' | 'production' | null>(null);

  const [wallet, setWallet] = useState<ProductionWallet | null>(null);
  const [testFlash, setTestFlash] = useState<string | null>(null);

  const [history, setHistory] = useState<DepositHistoryItem[]>([]);
  const [copied, setCopied] = useState(false);
  const [checking, setChecking] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Реактивное уведомление о новом зачислении — показывается
  // большим баннером поверх карточки когда поллер обнаружил
  // новый credited-депозит, которого ещё не было в previous history.
  // { id } для anti-retrigger — один и тот же депозит не показываем
  // дважды. Время жизни баннера — 15 секунд, затем скрывается
  // автоматически (юзер может закрыть вручную раньше).
  const [newlyCredited, setNewlyCredited] = useState<{
    id: number;
    amount: string;
    network: 'BSC' | 'TON';
    txHash: string;
  } | null>(null);
  // Set всех id депозитов, которые мы УЖЕ видели как credited.
  // Пополняется и при initial load, и при polling. Новыми считаем
  // только те, что reached статус credited после первой загрузки.
  const seenCreditedRef = useRef<Set<number>>(new Set());
  // Per-network флаг initial-load. Раньше был один общий ref, и после
  // первого пополнения в одной сети при переключении на другую сеть
  // её историю код считал "уже инициализированной" → существующие
  // credited-депозиты другой сети детектились как "новые" и баннер
  // повторно проигрывал звук + плашку при простой смене вкладки.
  const didInitialHistoryByNetworkRef = useRef<Record<Network, boolean>>({
    BSC: false,
    TON: false,
  });
  const bannerHideRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ----- Запрос wallet / test-deposit -----
  const requestDeposit = useCallback(async (n: Network) => {
    setLoading(true);
    setError(null);
    try {
      const resp: AxiosResponse<WalletResp> = await lunaApi.deposit.wallet(n);
      const data = resp.data;
      if (data.mode === 'test') {
        setMode('test');
        setWallet(null);
        setTestFlash(t('deposit.testFlash', { amount: formatUsdt(data.credited, locale) }));
        setTimeout(() => setTestFlash(null), 6000);
        onBalanceChange?.();
      } else {
        setMode('production');
        setWallet(data.wallet);
        loadHistory(n);
      }
    } catch (err) {
      setError(extractError(err, t('deposit.walletError')));
    } finally {
      setLoading(false);
    }
  }, [onBalanceChange]);

  // При смене сети — сбрасываем wallet, юзер ещё раз жмёт
  function onSelectNetwork(n: Network) {
    uiSounds.paymentTap();
    haptic.light();
    setNetwork(n);
    setWallet(null);
    setTestFlash(null);
    setError(null);
  }

  // ----- История -----
  // Помимо обновления списка — детектим новые credited-депозиты
  // (сравниваем id с seenCreditedRef) и, если появился свежий,
  // триггерим большой банер "Платёж получен!" поверх карточки +
  // звук + haptic + обновление баланса.
  const loadHistory = useCallback(async (n: Network) => {
    try {
      const r = await lunaApi.deposit.history(n, 10);
      const deposits = r.data.deposits || [];
      setHistory(deposits);

      const seen = seenCreditedRef.current;
      if (!didInitialHistoryByNetworkRef.current[n]) {
        // Первая загрузка ЭТОЙ сети — считаем всё что уже credited
        // "виденным", чтобы не триггерить банер на старых депозитах.
        for (const d of deposits) {
          if (d.status === 'credited') seen.add(d.id);
        }
        didInitialHistoryByNetworkRef.current[n] = true;
        return;
      }

      // Новые credited-депозиты с прошлой загрузки.
      const fresh = deposits.filter(
        (d) => d.status === 'credited' && !seen.has(d.id),
      );
      for (const d of fresh) seen.add(d.id);

      if (fresh.length > 0) {
        // Показываем самый свежий (первый в отсортированном DESC списке).
        const top = fresh[0]!;
        setNewlyCredited({
          id: top.id,
          amount: top.amount,
          network: top.network,
          txHash: top.tx_hash,
        });
        // Успех-haptic + звук зачисления
        try { haptic.success(); } catch { /* silent */ }
        try { uiSounds.slotConfirmed(); } catch { /* silent */ }
        // Обновить баланс в Dashboard/History и в этой карточке
        onBalanceChange?.();
        window.dispatchEvent(new CustomEvent('luna:deposit-done'));
        // Авто-скрытие через 15 секунд (юзер может закрыть раньше)
        if (bannerHideRef.current) clearTimeout(bannerHideRef.current);
        bannerHideRef.current = setTimeout(() => {
          setNewlyCredited(null);
        }, 15_000);
      }
    } catch {
      // silently — это не критично
    }
  }, [haptic, uiSounds, onBalanceChange]);

  // Авто-polling истории каждые 5 сек пока есть production-wallet.
  // 5 сек — компромисс: TON подтверждается ~5 сек, BSC ~36 сек.
  // Юзер увидит "Платёж получен" практически сразу после зачисления.
  useEffect(() => {
    if (mode !== 'production' || !wallet) return;
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => loadHistory(wallet.network), 5_000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [mode, wallet, loadHistory]);

  // Очистка таймаута баннера при unmount
  useEffect(() => {
    return () => {
      if (bannerHideRef.current) clearTimeout(bannerHideRef.current);
    };
  }, []);

  // На маунте — пробуем восстановить уже существующий кошелёк без
  // вызова create/credit. Если у юзера уже есть BSC или TON кошелёк
  // в БД, сразу показываем адрес и QR. Дёргаем read-only endpoint,
  // чтобы в test-режиме не всплывал случайный +1000.
  const didInitRef = useRef(false);
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const r = await lunaApi.deposit.walletStatus(network);
        if (cancelled) return;
        const d = r.data;
        if (d.mode === 'production' && d.wallet) {
          setMode('production');
          setWallet({
            id: d.wallet.id,
            address: d.wallet.address,
            network: d.wallet.network,
          });
          loadHistory(d.wallet.network);
        }
        // если wallet=null или mode=test — оставляем начальный экран,
        // юзер сам жмёт кнопку
      } catch {
        // молча — остаёмся на начальном экране
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [network, loadHistory]);

  async function onCopy() {
    if (!wallet) return;
    // Payment-тап звук + лёгкая микровибрация на сам тап кнопки,
    // мгновенный отклик ещё до того как clipboard сработает.
    uiSounds.paymentTap();
    haptic.light();
    try {
      await navigator.clipboard.writeText(wallet.address);
      setCopied(true);
      // Success-нотификация после фактического копирования — сигнал
      // что адрес действительно в буфере.
      haptic.success();
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // если clipboard API не доступен — тихо ошибка
      haptic.error();
    }
  }

  async function onCheckPaid() {
    if (!wallet) return;
    uiSounds.paymentTap();
    haptic.medium();
    setChecking(true);
    setError(null);
    try {
      const r = await lunaApi.deposit.check(wallet.network);
      if (r.data.processed > 0) {
        onBalanceChange?.();
      }
      loadHistory(wallet.network);
    } catch (err) {
      setError(extractError(err, t('deposit.checkError')));
    } finally {
      setChecking(false);
    }
  }

  // ------------------------------------------------------------------
  // RENDER
  // ------------------------------------------------------------------

  // Ещё ничего не жали — показываем выбор сети и большую кнопку
  if (!mode && !loading) {
    return (
      <GlassSurface className="p-5">
        <h3 className="mb-1 text-base font-medium text-gray-100">{t('deposit.depositTitle')}</h3>
        <p className="mb-4 text-xs text-gray-300">
          {t('deposit.depositHint')}
        </p>
        <NetworkSwitcher value={network} onChange={onSelectNetwork} />
        {error && (
          <div className="mb-3 rounded-lg border border-red-900/50 bg-red-950/30 p-3 text-xs text-red-300">
            <AlertCircle className="mb-1 inline h-3.5 w-3.5" /> {error}
          </div>
        )}
        <GlassButton onClick={() => { uiSounds.paymentTap(); haptic.medium(); requestDeposit(network); }} disabled={loading} className="w-full mt-4">
          {t('deposit.getAddress', { net: network })}
        </GlassButton>
      </GlassSurface>
    );
  }

  // Test-режим успех
  if (mode === 'test') {
    return (
      <GlassSurface className="p-5">
        <div className="mb-3 flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-amber-400" />
          <h3 className="text-base font-medium text-gray-100">{t('deposit.testModeTitle')}</h3>
        </div>
        {testFlash && (
          <div className="mb-4 rounded-lg border border-emerald-900/50 bg-emerald-950/30 p-3 text-sm text-emerald-300">
            <CheckCircle2 className="mb-0.5 mr-1 inline h-4 w-4" />
            {testFlash}
          </div>
        )}
        <p className="mb-4 text-xs text-gray-300">
          {t('deposit.testModeHint')}
        </p>
        <GlassButton onClick={() => { uiSounds.paymentTap(); haptic.success(); requestDeposit(network); }} disabled={loading} className="w-full">
          <Gift className="mr-2 inline h-4 w-4" />
          {t('deposit.testModeCta')}
        </GlassButton>
      </GlassSurface>
    );
  }

  // Production — wallet
  if (mode === 'production' && wallet) {
    const qrSrc = buildQrUrl(wallet.address);
    return (
      <GlassSurface className="p-4 relative overflow-hidden">
        {/* Реактивный банер "Платёж получен" — пульсирует поверх
            карточки, когда поллер обнаружил новый credited-депозит.
            Крупный, ярко-зелёный, с иконкой и суммой. */}
        <AnimatePresence>
          {newlyCredited && (
            <motion.div
              key={newlyCredited.id}
              initial={{ opacity: 0, y: -24, scale: 0.92 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -12, scale: 0.96 }}
              transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
              className="mb-3 rounded-2xl px-4 py-3 relative"
              style={{
                background:
                  'linear-gradient(135deg, rgba(16, 185, 129, 0.22), rgba(52, 211, 153, 0.1))',
                border: '1.5px solid rgba(16, 185, 129, 0.55)',
                boxShadow:
                  '0 0 0 1px rgba(16, 185, 129, 0.25), 0 10px 28px -8px rgba(16, 185, 129, 0.45), inset 0 1px 0 0 rgba(255,255,255,0.08)',
              }}
            >
              {/* Пульсирующий glow вокруг — чтобы поймать глаз */}
              <motion.div
                aria-hidden
                initial={{ opacity: 0.45 }}
                animate={{ opacity: [0.45, 0.85, 0.45] }}
                transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
                className="pointer-events-none absolute inset-0 rounded-2xl"
                style={{
                  boxShadow:
                    '0 0 26px 0 rgba(16, 185, 129, 0.55), inset 0 0 16px 0 rgba(16, 185, 129, 0.25)',
                }}
              />
              <div className="relative flex items-center gap-3">
                <motion.div
                  initial={{ scale: 0.5, rotate: -20 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', stiffness: 420, damping: 16, delay: 0.05 }}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
                  style={{
                    background:
                      'linear-gradient(135deg, rgba(16, 185, 129, 0.95), rgba(5, 150, 105, 0.85))',
                    boxShadow: '0 4px 14px -2px rgba(16, 185, 129, 0.7)',
                  }}
                >
                  <CheckCircle2 className="h-6 w-6 text-white" strokeWidth={2.4} />
                </motion.div>
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] font-extrabold text-emerald-200 leading-tight">
                    {t('deposit.receivedBanner')}
                  </div>
                  <div className="mt-0.5 text-[12px] text-emerald-100/90">
                    <b>{t('deposit.plusAmountNet', { amount: formatUsdt(newlyCredited.amount, locale) })}</b>
                    <span className="text-emerald-100/60">{t('deposit.netSuffix', { net: newlyCredited.network })}</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    uiSounds.paymentTap();
                    haptic.light();
                    if (bannerHideRef.current) clearTimeout(bannerHideRef.current);
                    setNewlyCredited(null);
                  }}
                  aria-label={t('deposit.closeAria')}
                  className="shrink-0 rounded-full p-1.5 text-emerald-200/70 hover:text-emerald-100 transition-colors"
                  style={{ background: 'rgba(255,255,255,0.06)' }}
                >
                  <XIcon className="h-3.5 w-3.5" strokeWidth={2.2} />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mb-2.5 flex items-center justify-between">
          <h3 className="text-[15px] font-medium text-gray-100">
            {t('deposit.prodTitle', { net: wallet.network })}
          </h3>
          <NetworkSwitcherCompact
            value={wallet.network}
            onChange={(n) => {
              onSelectNetwork(n);
              requestDeposit(n);
            }}
          />
        </div>

        <div className="mb-3 flex flex-col items-center gap-2">
          <div className="rounded-xl bg-white p-2">
            <img src={qrSrc} alt="QR" width={144} height={144} className="block" />
          </div>
          <div className="w-full">
            <div className="mb-1 text-[10px] uppercase tracking-wider text-gray-300">
              {t('deposit.addressFor')}
            </div>
            <div
              className="rounded-xl px-3 py-2 font-mono text-[11.5px] text-gray-200 break-all"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
            >
              {wallet.address}
            </div>
            <button
              onClick={onCopy}
              className="mt-1.5 inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[11px] font-medium text-purple-300 hover:text-purple-200 transition-colors"
              style={{ background: 'rgba(139, 92, 246, 0.08)', border: '1px solid rgba(139, 92, 246, 0.2)' }}
            >
              {copied ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? t('deposit.copied') : t('deposit.copyAddr')}
            </button>
          </div>
        </div>

        <div className="mb-3 rounded-xl px-3 py-2 text-[11px] leading-[1.4] text-gray-200"
          style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
        >
          <Trans
            i18nKey="deposit.sendHint"
            values={{
              net: wallet.network,
              delay: wallet.network === 'BSC' ? t('deposit.bsc36') : t('deposit.ton5'),
            }}
            components={{ b: <b /> }}
          />
        </div>

        {error && (
          <div className="mb-2 rounded-lg border border-red-900/50 bg-red-950/30 px-3 py-2 text-xs text-red-300">
            {error}
          </div>
        )}

        <div className="flex gap-2">
          <GlassButton onClick={onCheckPaid} disabled={checking} className="flex-1">
            {checking ? (
              <>
                <Loader2 className="mr-1.5 inline h-3.5 w-3.5 animate-spin" />
                {t('deposit.checking')}
              </>
            ) : (
              <>
                <RefreshCw className="mr-1.5 inline h-3.5 w-3.5" />
                {t('deposit.paidBtn')}
              </>
            )}
          </GlassButton>
        </div>

        {/* История */}
        {history.length > 0 && (
          <div className="mt-3">
            <div className="mb-1.5 text-[10px] uppercase tracking-wider text-gray-300">
              {t('deposit.recent')}
            </div>
            <div className="space-y-1">
              {history.slice(0, 5).map((h) => (
                <HistoryRow key={h.id} item={h} t={t} locale={locale} />
              ))}
            </div>
          </div>
        )}
      </GlassSurface>
    );
  }

  // Loading / fallback
  return (
    <GlassSurface className="p-5">
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-purple-400" />
      </div>
    </GlassSurface>
  );
}

// ----------------------------------------------------------------------------
// Помощники
// ----------------------------------------------------------------------------

function NetworkSwitcher({ value, onChange }: { value: Network; onChange: (n: Network) => void }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {(['BSC', 'TON'] as const).map((n) => (
        <button
          key={n}
          onClick={() => onChange(n)}
          className={`rounded-xl px-4 py-3 text-sm font-medium transition-colors ${
            value === n ? 'text-purple-200' : 'text-gray-300 hover:text-gray-300'
          }`}
          style={{
            background: value === n ? 'rgba(139, 92, 246, 0.12)' : 'rgba(255,255,255,0.03)',
            border: `1px solid ${value === n ? 'rgba(139, 92, 246, 0.35)' : 'rgba(255,255,255,0.06)'}`,
          }}
        >
          {n === 'BSC' ? 'USDT BEP-20' : 'USDT TON'}
        </button>
      ))}
    </div>
  );
}

function NetworkSwitcherCompact({ value, onChange }: { value: Network; onChange: (n: Network) => void }) {
  const next: Network = value === 'BSC' ? 'TON' : 'BSC';
  return (
    <button
      onClick={() => onChange(next)}
      className="rounded-lg px-2 py-1 text-[11px] font-medium text-purple-300 hover:text-purple-200"
      style={{ background: 'rgba(139, 92, 246, 0.08)', border: '1px solid rgba(139, 92, 246, 0.2)' }}
    >
      → {next}
    </button>
  );
}

function HistoryRow({ item, t, locale }: { item: DepositHistoryItem; t: TFunction; locale: string }) {
  const creditedLabel =
    item.status === 'credited' && item.credited_at
      ? t('deposit.historyCreditedAt', { when: formatMskDateTime(item.credited_at, locale) })
      : t('deposit.historyCredited');
  const statusMap: Record<DepositHistoryItem['status'], { label: string; color: string }> = {
    detected: { label: t('deposit.historyDetected'), color: 'text-amber-400' },
    confirming: { label: t('deposit.historyConfirming', { n: item.confirmations }), color: 'text-blue-400' },
    credited: { label: creditedLabel, color: 'text-emerald-400' },
    failed: { label: t('deposit.historyFailed'), color: 'text-red-400' },
  };
  const s = statusMap[item.status];
  return (
    <div
      className="flex items-center justify-between rounded-lg px-3 py-2"
      style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}
    >
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-300 font-medium">
          {formatUsdt(item.amount, locale)} USDT
        </span>
        <span className="text-[10px] text-gray-200">{item.network}</span>
      </div>
      <span className={`text-[11px] ${s.color}`}>{s.label}</span>
    </div>
  );
}

// Форматирует ISO timestamp → дата+время по локали (ru→ДД.ММ.ГГГГ, en→MM/DD/YYYY).
function formatMskDateTime(iso: string, locale: string = 'ru-RU'): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString(locale, {
      timeZone: 'Europe/Moscow',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return '';
  }
}

function formatUsdt(v: string | number, locale: string = 'ru-RU'): string {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (!isFinite(n)) return '0';
  return n.toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function buildQrUrl(data: string): string {
  // Внешний сервис, без доп. deps. Размер 240 покрывает Retina.
  const encoded = encodeURIComponent(data);
  return `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=4&data=${encoded}`;
}

function extractError(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { message?: string; error?: string } }; message?: string };
  return e.response?.data?.message || e.response?.data?.error || e.message || fallback;
}
